import React, { useState, useEffect, useRef } from 'react';

const ColorPicker = ({ label, value, onChange, placeholder = '#FFFFFF' }) => {
  const [color, setColor] = useState(value || placeholder);
  const [alpha, setAlpha] = useState(1);
  const [isOpen, setIsOpen] = useState(false);
  const colorPickerRef = useRef(null);

  // Convertir la valeur en couleur et alpha
  useEffect(() => {
    const currentValue = value || placeholder;
    if (currentValue === 'transparent') {
      setColor('#FFFFFF');
      setAlpha(0);
    } else if (currentValue.startsWith('rgba(')) {
      // Extraire les valeurs RGBA
      const match = currentValue.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
      if (match) {
        const [, r, g, b, a] = match;
        const hex = `#${parseInt(r).toString(16).padStart(2, '0')}${parseInt(g).toString(16).padStart(2, '0')}${parseInt(b).toString(16).padStart(2, '0')}`;
        setColor(hex);
        setAlpha(parseFloat(a));
      }
    } else if (currentValue.startsWith('#')) {
      setColor(currentValue);
      setAlpha(1);
    }
  }, [value, placeholder]);

  // Fermer le color picker au clic extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Convertir hex en RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  // Générer la valeur finale
  const generateValue = (newColor, newAlpha) => {
    if (newAlpha === 0) {
      return 'transparent';
    } else if (newAlpha === 1) {
      return newColor;
    } else {
      const rgb = hexToRgb(newColor);
      if (rgb) {
        return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${newAlpha})`;
      }
      return newColor;
    }
  };

  const handleColorChange = (newColor) => {
    setColor(newColor);
    onChange(generateValue(newColor, alpha));
  };

  const handleAlphaChange = (newAlpha) => {
    setAlpha(newAlpha);
    onChange(generateValue(color, newAlpha));
  };

  const handleTextChange = (textValue) => {
    onChange(textValue);
  };

  const getDisplayColor = () => {
    if (alpha === 0) return 'transparent';
    const rgb = hexToRgb(color);
    if (rgb && alpha < 1) {
      return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
    }
    return color;
  };

  return (
    <div className="form-group" ref={colorPickerRef} style={{ position: 'relative' }}>
      <label className="form-label">{label}</label>
      
      {/* Aperçu de la couleur et bouton d'ouverture */}
      <div 
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          cursor: 'pointer',
          padding: '8px',
          border: '2px solid var(--color-border)',
          borderRadius: '8px',
          backgroundColor: '#FFFFFF',
          transition: 'all 0.2s ease'
        }}
      >
        {/* Aperçu de la couleur avec damier pour transparence */}
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '6px',
          border: '1px solid var(--color-border)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Damier de transparence */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              linear-gradient(45deg, #ccc 25%, transparent 25%), 
              linear-gradient(-45deg, #ccc 25%, transparent 25%), 
              linear-gradient(45deg, transparent 75%, #ccc 75%), 
              linear-gradient(-45deg, transparent 75%, #ccc 75%)
            `,
            backgroundSize: '8px 8px',
            backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px'
          }} />
          {/* Couleur actuelle */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: getDisplayColor()
          }} />
        </div>
        
        {/* Valeur textuelle */}
        <span style={{ flex: 1, fontSize: '14px', color: 'var(--color-text)' }}>
          {value || placeholder}
        </span>
        
        {/* Icône d'ouverture */}
        <svg 
          width="16" 
          height="16" 
          viewBox="0 0 16 16" 
          fill="currentColor"
          style={{ 
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}
        >
          <path d="M4 6l4 4 4-4H4z"/>
        </svg>
      </div>

      {/* Panneau de sélection */}
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: '0',
          right: '0',
          zIndex: 1000,
          marginTop: '5px',
          padding: '15px',
          border: '2px solid var(--color-border)',
          borderRadius: '8px',
          backgroundColor: '#FFFFFF',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
        }}>
          {/* Sélecteur de couleur */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500' }}>
              Couleur
            </label>
            <input
              type="color"
              value={color}
              onChange={(e) => handleColorChange(e.target.value)}
              style={{
                width: '100%',
                height: '40px',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            />
          </div>

          {/* Slider d'opacité */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500' }}>
              Opacité: {Math.round(alpha * 100)}%
            </label>
            <div style={{ position: 'relative' }}>
              <style>{`
                .opacity-slider::-webkit-slider-thumb {
                  -webkit-appearance: none;
                  appearance: none;
                  width: 20px;
                  height: 20px;
                  background: var(--color-primary);
                  border-radius: 50%;
                  cursor: pointer;
                  border: 2px solid #ffffff;
                  box-shadow: 0 2px 6px rgba(255, 107, 53, 0.3);
                  transition: all 0.2s ease;
                }
                .opacity-slider::-webkit-slider-thumb:hover {
                  background: var(--color-primary-hover);
                  transform: scale(1.1);
                  box-shadow: 0 3px 8px rgba(255, 107, 53, 0.4);
                }
                .opacity-slider::-moz-range-thumb {
                  width: 20px;
                  height: 20px;
                  background: var(--color-primary);
                  border-radius: 50%;
                  cursor: pointer;
                  border: 2px solid #ffffff;
                  box-shadow: 0 2px 6px rgba(255, 107, 53, 0.3);
                  transition: all 0.2s ease;
                }
                .opacity-slider::-moz-range-thumb:hover {
                  background: var(--color-primary-hover);
                  transform: scale(1.1);
                  box-shadow: 0 3px 8px rgba(255, 107, 53, 0.4);
                }
                .opacity-slider::-moz-range-track {
                  height: 6px;
                  background: transparent;
                  border-radius: 3px;
                  border: none;
                }
              `}</style>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={alpha}
                onChange={(e) => handleAlphaChange(parseFloat(e.target.value))}
                className="opacity-slider"
                style={{
                  width: '100%',
                  margin: '10px 0',
                  WebkitAppearance: 'none',
                  appearance: 'none',
                  height: '6px',
                  background: `linear-gradient(to right, var(--color-primary) 0%, var(--color-primary) ${alpha * 100}%, var(--color-border-light) ${alpha * 100}%, var(--color-border-light) 100%)`,
                  borderRadius: '3px',
                  outline: 'none',
                  transition: 'all 0.3s ease'
                }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-light)', marginTop: '5px' }}>
              <span>Transparent</span>
              <span>Opaque</span>
            </div>
          </div>

          {/* Champ de saisie manuelle */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500' }}>
              Valeur personnalisée
            </label>
            <input
              type="text"
              value={value}
              onChange={(e) => handleTextChange(e.target.value)}
              className="form-input"
              style={{ width: '100%', fontSize: '14px' }}
              placeholder={placeholder}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorPicker;